import React from "react"
import { Helmet } from "react-helmet"
import { connect } from "react-redux"
import {
	delayedDispatch,
	setBreadcrumb,
	setTitle,
	updateCrumb,
	setLoader,
} from "store/actions"
import { push } from "connected-react-router"
import { Props } from "./types"
import {
	Col,
	Input,
	Modal,
	notification,
	Row,
	Select,
	Switch,
	Tabs,
	Tooltip,
} from "antd"
import { ContentWrapper, Icon, Table } from "components"
import { QuestionCircleOutlined } from "@ant-design/icons"
import Dropzone from "react-dropzone"
import Compressor from "compressorjs"
import { API, Endpoints } from "utils/api"
import { LANGUAGES, translate } from "utils/utils"
import { DateTime } from "luxon"
import Strings from "utils/strings"
import "./styles.scss"

// @ts-ignore
const { Option } = Select

export class BusinessDetail extends React.Component<Props, any> {
	phoneValidationInput: any
	constructor(props: Props) {
		super(props)

		this.state = {
			activeTab: "info",
			isActive: true,
			name: "",
			postalCode: "",
			country: null,
			city: "",
			state: null,
			address: "",
			address2: "",
			language: "pt",
			hasUnsavedFields: false,
			defaultCountries: [],
			countriesOptions: [],
			defaultStates: [],
			statesOptions: [],
			cityOptions: [],
			notDeliveredReasons: [],
			internalNotDeliveredReasons: {},
			selectedReasonLanguage: null,
			apiCallSetups: [],
		}
	}

	componentDidMount() {
		const { dispatch, match } = this.props

		dispatch(setTitle(Strings.sidebar.businesses))

		delayedDispatch(
			setBreadcrumb(() => {
				return {
					locations: [
						{
							icon: "partner",
							text: Strings.sidebar.businesses,
							route: "/businesses",
						},
						{
							text:
								match.params.id === "new"
									? Strings.businesses.new
									: translate(this.state.business?.name),
							icon: match.params.id === "new" ? "plus" : "pencil-outline",
						},
					],
					actions: [
						{
							type: "switch",
							value: this.state.isActive,
							onClick: (value: boolean) =>
								this.setState({ isActive: value, hasUnsavedFields: true }),
							text: Strings.generic.active,
							small: {
								label: true,
								switch: true,
							},
						},
						{
							type: "button",
							text: Strings.generic.save,
							disabled: !this.state.hasUnsavedFields,
							className: "BreadcrumbSaveButton",
							onClick: this.handleSubmit,
							separator: "left",
							isSave: true,
						},
					],
				}
			})
		)

		this.getData()
	}

	componentDidUpdate() {
		const { business } = this.state
		const { dispatch } = this.props

		dispatch(updateCrumb())
		dispatch(
			setTitle(
				`${Strings.sidebar.businesses} - ${business?.name || Strings.businesses.new}`
			)
		)
	}

	getData = async (requestCountries = true) => {
		const { dispatch, match } = this.props

		try {
			dispatch(setLoader(true))

			const promises = []

			// Fetch countries data if requested
			if (requestCountries) {
				promises.push(
					API.get({
						url: Endpoints.uriCountries(),
					})
				)
			}

			// Fetch business data if not creating a new business
			if (match?.params?.id !== "new") {
				promises.push(
					API.get({
						url: Endpoints.uriBusinesses(match?.params?.id),
					})
				)
			}

			// Fetch internal not delivered reasons
			promises.push(
				API.get({
					url: Endpoints.uriSettings("non_delivery_reasons"),
				})
			)

			// Fetch all notification templates for the selects
			promises.push(
				API.get({
					url: Endpoints.uriNotificationTemplates(),
				})
			)

			// Await all API responses
			const responses = await Promise.all(promises)

			// Handle responses based on which requests were made
			let responseIndex = 0

			// Process countries response if it was requested
			if (requestCountries && responses[responseIndex]?.ok) {
				const countriesResponse = responses[responseIndex]
				const defaultCountries = countriesResponse.data.results.countries || []
				const defaultStates: any = {}
				defaultCountries.forEach(
					(country: any) => (defaultStates[country._id] = country?.states || [])
				)

				this.setState({
					defaultCountries,
					countriesOptions: defaultCountries?.map(
						(elem: any, index: number) => (
							<Option key={`country_elem_${index}`} value={elem._id}>
								{elem.name}
							</Option>
						)
					),
					defaultStates,
				})
				responseIndex++
			}

			// Process business response if it was requested
			if (match?.params?.id !== "new" && responses[responseIndex]?.ok) {
				const businessResponse = responses[responseIndex]
				const { business = {} } = businessResponse.data.results

				const notDeliveredReasonsArray = []
				if (business.notDeliveredReasons) {
					for (const [key, value] of Object.entries(
						business.notDeliveredReasons
					)) {
						notDeliveredReasonsArray.push({
							internalCode: key,
							...(typeof value === "object" && value !== null ? value : {}),
						})
					}
				}

				this.setState({
					business,
					...business,
					notDeliveredReasons: notDeliveredReasonsArray,
					apiCallSetups: business.apiCallSetups || [],
				})
			}

			// Process settings response for internal not delivered reasons
			const settingsResponse = responses[responses.length - 2]
			if (settingsResponse?.ok) {
				const reasons = settingsResponse.data.results?.settings?.reasons
				this.setState({
					internalNotDeliveredReasons: reasons || {},
				})
			}

			// Process notification templates response
			const notifTemplatesResponse = responses[responses.length - 1]
			if (notifTemplatesResponse?.ok) {
				const {
					notificationTemplates: allNotificationTemplates = [],
					availableEvents = {},
				} = notifTemplatesResponse.data.results || {}
				this.setState({ allNotificationTemplates, availableEvents })
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		} finally {
			dispatch(setLoader(false))
		}
	}

	validBusiness() {
		const { name, country, state, address, notDeliveredReasons } = this.state

		if (!name?.trim()) {
			notification.warn({
				message: Strings.errors.invalidFields,
				description: Strings.businesses.invalidName,
				placement: "bottomRight",
				duration: 5,
			})

			return false
		}

		if (!country) {
			notification.warn({
				message: Strings.errors.invalidFields,
				description: Strings.businesses.emptyCountry,
				placement: "bottomRight",
				duration: 5,
			})

			return false
		}

		if (!state) {
			notification.warn({
				message: Strings.errors.invalidFields,
				description: Strings.businesses.emptyState,
				placement: "bottomRight",
				duration: 5,
			})

			return false
		}

		if (!address?.trim()) {
			notification.warn({
				message: Strings.errors.invalidFields,
				description: Strings.businesses.emptyAddress,
				placement: "bottomRight",
				duration: 5,
			})

			return false
		}

		if (notDeliveredReasons?.length > 0) {
			// Check if all reasons have internalCode, reasonText and dingooCode
			for (const reason of notDeliveredReasons) {
				if (!reason.internalCode || !reason.dingooCode) {
					notification.warn({
						message: Strings.errors.invalidFields,
						description: Strings.businesses.notDeliveredReasonsMissingFields,
						placement: "bottomRight",
						duration: 5,
					})

					return false
				}
			}

			// Check if all reasons have text in at least one common language
			const allReasonLanguages = notDeliveredReasons.map((reason: any) =>
				LANGUAGES.filter(
					(lang: any) => reason[lang.value] && reason[lang.value].trim()
				).map((lang: any) => lang.value)
			)

			// Find common languages across all reasons
			const commonLanguages: string[] = allReasonLanguages.reduce(
				(common: string[], reasonLangs: string[]) =>
					common.filter((lang: string) => reasonLangs.includes(lang))
			)

			if (commonLanguages.length === 0) {
				notification.warn({
					message: Strings.errors.invalidFields,
					description: Strings.businesses.notDeliveredReasonsMissingLanguage,
					placement: "bottomRight",
					duration: 5,
				})

				return false
			}

			// Check for duplicate internalCodes
			const internalCodes = notDeliveredReasons.map((r: any) => r.internalCode)
			const uniqueInternalCodes = new Set(internalCodes)
			if (uniqueInternalCodes.size !== internalCodes.length) {
				notification.warn({
					message: Strings.errors.invalidFields,
					description: Strings.businesses.duplicateInternalCode,
					placement: "bottomRight",
					duration: 5,
				})

				return false
			}
		}

		return true
	}

	handleSubmit = async () => {
		const {
			name,
			postalCode,
			country,
			city,
			address,
			address2,
			state,
			logo,
			notDeliveredReasons,
			labelTemplate,
			apiCallSetups,
			notifications,
		} = this.state
		const { dispatch, match } = this.props

		if (!this.validBusiness()) return

		dispatch(setLoader(true))

		const request = match.params.id === "new" ? API.post : API.put

		const body = new FormData()

		body.append("name", name)
		body.append("postalCode", postalCode || "")
		body.append("country", country)
		body.append("city", city || "")
		body.append("address", address)
		body.append("address2", address2 || "")
		body.append("state", state)
		body.append("labelTemplate", labelTemplate?._id || null)

		if (notifications && Object.keys(notifications).length > 0) {
			const notificationsIds: Record<string, string | null> = {}
			for (const [key, val] of Object.entries(
				notifications as Record<string, any>
			)) {
				notificationsIds[key] = (val as any)?._id || val || null
			}
			body.append("notifications", JSON.stringify(notificationsIds))
		}

		if (notDeliveredReasons?.length > 0) {
			// Transform notDeliveredReasons from array to object with keys
			const notDeliveredReasonsObj = notDeliveredReasons.reduce(
				(acc: any, reason: any) => {
					const { internalCode, ...rest } = reason
					acc[internalCode] = rest
					return acc
				},
				{}
			)

			// Append to body
			body.append("notDeliveredReasons", JSON.stringify(notDeliveredReasonsObj))
		}

		if (apiCallSetups?.length > 0) {
			body.append("apiCallSetups", JSON.stringify(apiCallSetups))
		}

		if (logo && Object.keys(logo).length > 0) {
			if (logo.file) {
				body.append("logo", logo.file)
			} else {
				body.append("logo", logo)
			}
		}

		try {
			const response = await request({
				url: Endpoints.uriBusinesses(
					match?.params?.id === "new" ? "" : match?.params?.id
				),
				data: body,
			})

			if (response?.ok) {
				const { business = {} } = response.data.results

				const notDeliveredReasonsArray = []
				if (business.notDeliveredReasons) {
					for (const [key, value] of Object.entries(
						business.notDeliveredReasons
					)) {
						notDeliveredReasonsArray.push({
							internalCode: key,
							...(typeof value === "object" && value !== null ? value : {}),
						})
					}
				}

				this.setState({
					business,
					...business,
					notDeliveredReasons: notDeliveredReasonsArray,
					apiCallSetups: business.apiCallSetups || [],
					hasUnsavedFields: false,
				})

				notification.success({
					message: Strings.businesses.header,
					description: Strings.businesses.edited,
					placement: "bottomRight",
					duration: 5,
				})

				if (match?.params?.id === "new") {
					const { users } = business
					this.setState({ users })
					dispatch(push("/businesses"))
					dispatch(push(`/businesses/${business._id}`))
				}
			} else {
				notification.error({
					message: Strings.serverErrors.title,
					description: response.data.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}

		dispatch(setLoader(false))
	}

	getBase64(file: any) {
		return new Promise((resolve, reject) => {
			const reader = new FileReader()
			reader.readAsDataURL(file)
			reader.onload = () => resolve(reader.result)
			reader.onerror = (error) => reject(error)
		})
	}

	onDrop(files: any, isLogo = false) {
		try {
			const file = files[files.length - 1]

			new Compressor(file, {
				quality: 0.9,
				maxWidth: 400,
				mimeType: "image/jpeg",
				success: (result: any) => {
					this.getBase64(result).then((res) => {
						if (isLogo) {
							this.setState({
								logo: { file: result, preview: res },
								hasUnsavedFields: true,
							})
						}
					})
				},
			})
		} catch (err) {
			console.log("err", err)
			notification.warn({
				message: Strings.errors.unsupportedFile,
				description: Strings.errors.fileNotSupported,
				placement: "bottomRight",
				duration: 5,
			})
		}
	}

	async addContract() {
		const { tempContract, business } = this.state
		const { dispatch } = this.props

		dispatch(setLoader(true))

		let response: any
		try {
			const request = tempContract?._id ? API.put : API.post
			response = await request({
				url: Endpoints.uriBusinesses(
					`${business._id}/contracts/${tempContract?._id || ""}`
				),
				data: {
					...tempContract,
				},
			})

			if (response.ok) {
				await this.getData(false)

				notification.success({
					message: Strings.businesses.contracts,
					description: tempContract?._id
						? Strings.businesses.contractEdited
						: Strings.businesses.contractAdded,
					placement: "bottomRight",
					duration: 5,
				})

				this.setState({ tempContract: {}, openContractDrawer: false })
			} else {
				notification.error({
					message: Strings.businesses.contracts,
					description:
						response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.businesses.contracts,
				description:
					response?.data?.message ||
					(err as string) ||
					Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}

		dispatch(setLoader(false))
	}

	async deleteContract(id: string) {
		const { business } = this.state
		const { dispatch } = this.props

		dispatch(setLoader(true))

		let response: any
		try {
			response = await API.delete({
				url: Endpoints.uriBusinesses(`${business._id}/contracts/${id}`),
			})

			if (response.ok) {
				await this.getData()

				notification.success({
					message: Strings.businesses.contracts,
					description: Strings.businesses.contractRemoved,
					placement: "bottomRight",
					duration: 5,
				})
			} else {
				notification.error({
					message: Strings.businesses.contracts,
					description:
						response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.businesses.contracts,
				description:
					response?.data?.message ||
					(err as string) ||
					Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}

		dispatch(setLoader(false))
	}

	renderGeneralInfo() {
		const {
			name,
			address,
			address2,
			postalCode,
			country,
			city,
			state,
			logo,
			logoSignedUrl,
			countriesOptions,
			defaultStates,
			labelTemplate,
			labels,
		} = this.state

		return (
			<Row gutter={[20, 10]}>
				<Col xs={24} md={6}>
					<label htmlFor="image_upload" className="InputLabel">
						{Strings.fields.logo}
					</label>
					<div className="BusinessMainImage">
						<Dropzone
							accept="image/jpg, image/jpeg, image/png"
							className="GenericImageUpload"
							id="image_upload"
							onDrop={(files: any) => this.onDrop(files, true)}
						>
							{logo ? (
								<div
									className="BusinessImage"
									style={{
										backgroundImage:
											(logo &&
												`url('${logo?.preview || logoSignedUrl || logo}')`) ||
											"none",
										backgroundSize: "contain",
									}}
								/>
							) : (
								<div
									className={`GenericImageUploadOverlay${!logo ? " --visible" : ""}`}
								>
									<Icon name="frame" />
									<span>{Strings.generic.changeImage}</span>
								</div>
							)}
							{logo && (
								<button
									onClick={(e: React.MouseEvent<HTMLElement>) => {
										e.stopPropagation()
										this.setState({ logo: null, hasUnsavedFields: true })
									}}
									className="GenericImageDelete"
								>
									<Icon name="close" />
								</button>
							)}
						</Dropzone>
					</div>
				</Col>
				<Col xs={24} md={18}>
					<Row gutter={[20, 10]}>
						<Col xs={24} md={24}>
							<label
								htmlFor="business_name"
								className="InputLabel --label-required"
							>
								{Strings.fields.name}
							</label>
							<Input
								id="business_name"
								value={name || ""}
								placeholder={Strings.fields.name}
								onChange={(event: any) => {
									const value = event.target.value
									this.setState({ name: value, hasUnsavedFields: true })
								}}
							/>
						</Col>
					</Row>
					<Row gutter={[20, 10]}>
						<Col xs={24} md={12}>
							<label
								htmlFor="business_address_1"
								className="InputLabel --label-required"
							>
								{Strings.businesses.addressLine1}
							</label>
							<Input
								id="business_address_1"
								value={address || ""}
								placeholder={Strings.businesses.addressLine1}
								onChange={(event: any) => {
									const value = event.target.value
									this.setState({ address: value, hasUnsavedFields: true })
								}}
							/>
						</Col>
						<Col xs={24} md={6}>
							<label htmlFor="business_address_2" className="InputLabel">
								{Strings.businesses.addressLine2}
							</label>
							<Input
								id="business_address_2"
								value={address2 || ""}
								placeholder={Strings.businesses.addressLine2}
								onChange={(event: any) => {
									const value = event.target.value
									this.setState({ address2: value, hasUnsavedFields: true })
								}}
							/>
						</Col>
						<Col xs={24} md={6}>
							<label htmlFor="postal_code" className="InputLabel">
								{Strings.fields.zipCode}
							</label>
							<Input
								id="postal_code"
								placeholder={Strings.fields.zipCode}
								value={postalCode || ""}
								onChange={(event: any) => {
									const value = event.target.value
									this.setState({ postalCode: value, hasUnsavedFields: true })
								}}
							/>
						</Col>
					</Row>
					<Row gutter={[20, 10]}>
						<Col xs={24} md={8}>
							<label htmlFor="country" className="InputLabel --label-required">
								{Strings.fields.country}
							</label>
							<Select
								key={`country_${country}`}
								className="tagsSelector"
								style={{ width: "100%" }}
								placeholder={Strings.fields.country}
								showSearch
								disabled={false}
								filterOption={(input: any, option: any) =>
									option.children.toLowerCase().indexOf(input.toLowerCase()) >=
									0
								}
								value={country || null}
								onChange={(elem: any) => {
									this.setState({
										country: elem,
										state: null,
										hasUnsavedFields: true,
									})
								}}
							>
								{countriesOptions}
							</Select>
						</Col>
						<Col xs={24} md={8}>
							<label htmlFor="state" className="InputLabel --label-required">
								{Strings.fields.state}
							</label>
							<Select
								id="state"
								key={`state_${state}`}
								className="tagsSelector"
								style={{ width: "100%" }}
								placeholder={Strings.fields.state}
								showSearch
								filterOption={(input: any, option: any) =>
									option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
								}
								value={state || null}
								disabled={!country}
								options={
									country && defaultStates[country]
										? defaultStates[country].map((state: any) => ({
												value: state?._id,
												label: state?.name,
											}))
										: []
								}
								onChange={(elem: any) => {
									this.setState({
										state: elem,
										hasUnsavedFields: true,
									})
								}}
							/>
						</Col>
						<Col xs={24} md={8}>
							<label htmlFor="city" className="InputLabel">
								{Strings.fields.city}
							</label>
							<Input
								id="city"
								placeholder={Strings.fields.city}
								value={city || ""}
								onChange={(event: any) => {
									const value = event.target.value
									this.setState({
										city: value,
										hasUnsavedFields: true,
									})
								}}
							/>
						</Col>
					</Row>
					<Row gutter={[20, 10]}>
						<Col xs={24}>
							<label htmlFor="label_template" className="InputLabel">
								{Strings.fields.labelTemplate}
							</label>
							<Select
								id="label_template"
								style={{ width: "100%" }}
								placeholder={Strings.fields.labelTemplate}
								allowClear
								showSearch
								filterOption={(input: any, option: any) =>
									option.children.toLowerCase().indexOf(input.toLowerCase()) >=
									0
								}
								value={labelTemplate?._id || null}
								onChange={(value: any) => {
									const selected =
										(labels || []).find((l: any) => l._id === value) || null
									this.setState({
										labelTemplate: selected,
										hasUnsavedFields: true,
									})
								}}
							>
								{(labels || []).map((l: any) => (
									<Select.Option key={l._id} value={l._id}>
										{l.name}
									</Select.Option>
								))}
							</Select>
						</Col>
					</Row>
				</Col>
			</Row>
		)
	}

	renderContracts() {
		const { contracts } = this.state
		const { dispatch, match } = this.props

		return (
			<Table
				title={{
					icon: "file",
					title: Strings.businesses.contracts,
				}}
				data={contracts}
				columns={[
					{
						Header: Strings.contracts.name,
						id: "name",
						accessor: (row: any) => (row.type && row.name) || "-",
						type: "text",
					},
					{
						Header: Strings.businesses.typeContract,
						id: "type",
						accessor: (row: any) => (row.type && row.type.name) || "-",
						type: "text",
					},
					{
						Header: Strings.businesses.descriptionContract,
						id: "description",
						accessor: (row: any) => row.description || "-",
						type: "text",
					},
					{
						Header: Strings.fields.startDate,
						id: "startDate",
						accessor: (row: any) =>
							(row.startDate &&
								DateTime.fromISO(row.startDate).toFormat("dd/LL/yyyy")) ||
							"-",
						type: "date",
						Filter: () => {},
						maxWidth: 200,
					},
					{
						Header: Strings.fields.endDate,
						id: "endDate",
						accessor: (row: any) =>
							(row.endDate &&
								DateTime.fromISO(row.endDate).toFormat("dd/LL/yyyy")) ||
							"-",
						type: "date",
						Filter: () => {},
						maxWidth: 200,
					},
				]}
				filterable
				sortable
				add={{
					onClick: () =>
						dispatch(push(`/businesses/${match.params.id}/contracts/new`)),
				}}
				actions={{
					edit: (original: any) => ({
						onClick: () =>
							dispatch(
								push(`/businesses/${match.params.id}/contracts/${original._id}`)
							),
					}),
					remove: (original: any) => ({
						onClick: () => this.deleteContract(original._id),
					}),
				}}
			/>
		)
	}

	renderNotDeliveredReasons() {
		const { notDeliveredReasons, internalNotDeliveredReasons } = this.state
		const currentLanguage =
			this.state.selectedReasonLanguage || this.props.language || "pt"

		return (
			<ContentWrapper>
				<div style={{ marginBottom: 10 }} className="MultiTitleSeparator">
					<h2>
						{Strings.businesses.notDeliveredReasons}
						<Tooltip
							placement="top"
							title={Strings.businesses.notDeliveredReasonsHelper}
						>
							<QuestionCircleOutlined
								style={{
									marginLeft: 6,
									color: "#ff9800",
									cursor: "help",
									fontSize: "16px",
									verticalAlign: "top",
								}}
							/>
						</Tooltip>
					</h2>
					<div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
						<Select
							size="small"
							style={{ width: "60px", marginBottom: "2px" }}
							value={currentLanguage}
							onChange={(lang: string) => {
								this.setState({ selectedReasonLanguage: lang })
							}}
						>
							{LANGUAGES.map((lang: any) => (
								<Select.Option key={lang.value} value={lang.value}>
									{lang.label}
								</Select.Option>
							))}
						</Select>
						<button
							style={{
								height: "24px",
								minHeight: "24px",
								width: "24px",
								minWidth: "24px",
								marginBottom: "3px",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
							onClick={() => {
								let newNotDeliveredReasons = [...notDeliveredReasons]
								newNotDeliveredReasons.push({
									internalCode: "",
									externalCode: "",
									dingooCode: "",
									pt: "",
									en: "",
									active: true,
								})
								this.setState({
									notDeliveredReasons: newNotDeliveredReasons,
									hasUnsavedFields: true,
								})
							}}
						>
							<Icon name="plus" style={{ fontSize: "12px" }} />
						</button>
					</div>
				</div>
				{/* Header Row with Labels */}
				<Row
					gutter={[10, 10]}
					align="middle"
					style={{ marginBottom: 10 }}
					className="d-none d-md-flex"
				>
					<Col xs={24} md={3}>
						<label className="InputLabel --label-required">
							{Strings.businesses.internalCode}
						</label>
					</Col>
					<Col xs={24} md={3}>
						<label className="InputLabel">
							{Strings.businesses.externalCode}
						</label>
					</Col>
					<Col xs={24} md={9} xxl={11}>
						<label className="InputLabel --label-required">
							{Strings.businesses.reasonText}
						</label>
					</Col>
					<Col xs={24} md={5}>
						<label className="InputLabel --label-required">
							{Strings.businesses.internalReason}
						</label>
					</Col>
					<Col xs={12} md={2} xxl={1}>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<label className="InputLabel">{Strings.generic.active}</label>
						</div>
					</Col>
					<Col xs={12} md={2} xxl={1}>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<label className="InputLabel">{Strings.generic.delete}</label>
						</div>
					</Col>
				</Row>
				{/* Data Rows */}
				<Row gutter={[20, 10]}>
					{notDeliveredReasons.map((reason: any, index: number) => {
						return (
							<Col xs={24} md={24} key={`not_delivered_reason_${index}`}>
								<Row gutter={[10, 10]} align="middle">
									<Col xs={24} md={3}>
										<label className="InputLabel --label-required d-md-none">
											{Strings.businesses.internalCode}
										</label>
										<Input
											id={`internal_code_${index}`}
											placeholder={Strings.businesses.internalCode}
											value={reason.internalCode || ""}
											onChange={(e: any) => {
												// Only allow alphanumeric, underscore and hyphen
												if (!e.target.value.match(/^[a-zA-Z0-9_-]*$/)) {
													return
												}
												this.setState({
													notDeliveredReasons: notDeliveredReasons.map(
														(reason: any, i: number) =>
															i === index
																? { ...reason, internalCode: e.target.value }
																: reason
													),
													hasUnsavedFields: true,
												})
											}}
										/>
									</Col>
									<Col xs={24} md={3}>
										<label className="InputLabel d-md-none">
											{Strings.businesses.externalCode}
										</label>
										<Input
											id={`external_code_${index}`}
											placeholder={Strings.businesses.externalCode}
											value={reason.externalCode || ""}
											onChange={(e: any) => {
												let newNotDeliveredReasons = [...notDeliveredReasons]

												newNotDeliveredReasons[index] = {
													...newNotDeliveredReasons[index],
													externalCode: e.target.value,
												}
												this.setState({
													notDeliveredReasons: newNotDeliveredReasons,
													hasUnsavedFields: true,
												})
											}}
										/>
									</Col>
									<Col xs={24} md={9} xxl={11}>
										<label className="InputLabel --label-required d-md-none">
											{Strings.businesses.reasonText}
										</label>
										<Input
											id={`reason_text_${index}`}
											placeholder={Strings.businesses.reasonText}
											value={reason[currentLanguage] || ""}
											onChange={(e: any) => {
												this.setState({
													notDeliveredReasons: notDeliveredReasons.map(
														(reason: any, i: number) =>
															i === index
																? {
																		...reason,
																		[currentLanguage]: e.target.value,
																	}
																: reason
													),
													hasUnsavedFields: true,
												})
											}}
										/>
									</Col>
									<Col xs={24} md={5}>
										<label className="InputLabel --label-required d-md-none">
											{Strings.businesses.internalCode}
										</label>
										<Select
											id={`internal_reason_${index}`}
											style={{ width: "100%" }}
											placeholder={Strings.businesses.internalReason}
											value={reason.dingooCode || undefined}
											onChange={(value: string) => {
												this.setState({
													notDeliveredReasons: notDeliveredReasons.map(
														(reason: any, i: number) =>
															i === index
																? { ...reason, dingooCode: value }
																: reason
													),
													hasUnsavedFields: true,
												})
											}}
										>
											{Object.entries(internalNotDeliveredReasons).map(
												([key, internalReason]: [string, any]) => (
													<Select.Option
														key={`internal_reason_option_${key}_${index}`}
														value={key}
													>
														{internalReason[currentLanguage]}
													</Select.Option>
												)
											)}
										</Select>
									</Col>

									<Col xs={12} md={2} xxl={1}>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
											}}
										>
											<label className="InputLabel d-md-none">
												{Strings.generic.active}
											</label>
										</div>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
											}}
										>
											<Switch
												checked={reason.active !== false}
												onChange={(checked: boolean) => {
													let newNotDeliveredReasons = [...notDeliveredReasons]
													newNotDeliveredReasons[index] = {
														...newNotDeliveredReasons[index],
														active: checked,
													}
													this.setState({
														notDeliveredReasons: newNotDeliveredReasons,
														hasUnsavedFields: true,
													})
												}}
											/>
										</div>
									</Col>

									<Col xs={12} md={2} xxl={1}>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
											}}
										>
											<label className="InputLabel d-md-none">
												{Strings.generic.delete}
											</label>
										</div>
										<div
											className="NotDeliveredDeleteContainer"
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
											}}
										>
											<button
												className="NotDeliveredDeleteButton"
												onClick={() => {
													Modal.confirm({
														title: Strings.generic.delete,
														content: Strings.generic.deleteMessage,
														okText: Strings.generic.confirm,
														cancelText: Strings.generic.cancel,
														onOk: () => {
															this.setState({
																notDeliveredReasons: notDeliveredReasons.filter(
																	(_: any, i: number) => i !== index
																),
																hasUnsavedFields: true,
															})
														},
													})
												}}
											>
												<Icon name="trash" />
											</button>
										</div>
									</Col>
								</Row>
							</Col>
						)
					})}
				</Row>
			</ContentWrapper>
		)
	}

	renderApiCallSetup() {
		const { apiCallSetups = [] } = this.state

		const openHelpModal = () => {
			Modal.info({
				title: Strings.businesses.apiCallHelpTitle,
				width: 600,
				content: (
					<div style={{ lineHeight: 1.7 }}>
						<p>
							<strong>{Strings.businesses.apiCallHelpUrlLabel}:</strong>{" "}
							{Strings.businesses.apiCallHelpUrlDesc}
						</p>
						<p>
							<strong>{Strings.businesses.apiCallHelpMethodLabel}:</strong>{" "}
							{Strings.businesses.apiCallHelpMethodDesc}
						</p>
						<p>
							<strong>{Strings.businesses.apiCallHelpAuthLabel}:</strong>{" "}
							{Strings.businesses.apiCallHelpAuthDesc}
						</p>
						<p>
							<strong>{Strings.businesses.apiCallHelpBodySchemaLabel}:</strong>{" "}
							{Strings.businesses.apiCallHelpBodySchemaDesc}
						</p>
					</div>
				),
				okText: Strings.generic.close,
			})
		}

		const updateSetup = (index: number, patch: any) => {
			const next = [...apiCallSetups]
			next[index] = { ...next[index], ...patch }
			this.setState({ apiCallSetups: next, hasUnsavedFields: true })
		}

		const updateAuthConfig = (index: number, patch: any) => {
			const next = [...apiCallSetups]
			next[index] = {
				...next[index],
				authConfig: { ...(next[index].authConfig || {}), ...patch },
			}
			this.setState({ apiCallSetups: next, hasUnsavedFields: true })
		}

		return (
			<ContentWrapper>
				<div style={{ marginBottom: 10 }} className="MultiTitleSeparator">
					<h2>
						{Strings.businesses.apiCallSetup}
						<Tooltip
							placement="top"
							title={Strings.businesses.apiCallSetupHelper}
						>
							<QuestionCircleOutlined
								style={{
									marginLeft: 6,
									color: "#ff9800",
									cursor: "help",
									fontSize: "16px",
									verticalAlign: "top",
								}}
							/>
						</Tooltip>
					</h2>
					<button
						style={{
							height: "24px",
							minHeight: "24px",
							width: "24px",
							minWidth: "24px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
						onClick={() => {
							const next = [...apiCallSetups]
							next.push({
								name: "",
								active: true,
								trigger: undefined,
								url: "",
								method: "POST",
								authMethod: "none",
								authConfig: {},
								bodySchema: "{}",
							})
							this.setState({ apiCallSetups: next, hasUnsavedFields: true })
						}}
					>
						<Icon name="plus" style={{ fontSize: "12px" }} />
					</button>
				</div>

				{apiCallSetups.map((setup: any, index: number) => (
					<div key={`api_call_setup_${index}`} className="ApiCallSetupCard">
						{/* Row 1: Name · URL · Method · Active · Help · Delete */}
						<Row gutter={[10, 10]} align="middle" style={{ marginBottom: 8 }}>
							<Col xs={24} md={4}>
								<label className="InputLabel --label-required">
									{Strings.businesses.apiCallName}
								</label>
								<Input
									placeholder={Strings.businesses.apiCallName}
									value={setup.name || ""}
									onChange={(e: any) =>
										updateSetup(index, { name: e.target.value })
									}
								/>
							</Col>
							<Col xs={24} md={4}>
								<label className="InputLabel --label-required">
									{Strings.businesses.apiCallTrigger}
								</label>
								<Select
									style={{ width: "100%" }}
									placeholder={Strings.businesses.apiCallTriggerPlaceholder}
									value={setup.trigger || undefined}
									allowClear
									onChange={(value: string) =>
										updateSetup(index, { trigger: value })
									}
								>
									<Select.Option value="onRouteStart">
										{Strings.businesses.apiCallTriggerOnRouteStart}
									</Select.Option>
								</Select>
							</Col>
							<Col xs={24} md={9}>
								<label className="InputLabel --label-required">
									{Strings.businesses.apiCallUrl}
								</label>
								<Input
									placeholder="https://api.example.com/endpoint"
									value={setup.url || ""}
									onChange={(e: any) =>
										updateSetup(index, { url: e.target.value })
									}
								/>
							</Col>
							<Col xs={24} md={3}>
								<label className="InputLabel">
									{Strings.businesses.apiCallMethod}
								</label>
								<Select
									style={{ width: "100%" }}
									value={setup.method || "POST"}
									onChange={(value: string) =>
										updateSetup(index, { method: value })
									}
								>
									{["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => (
										<Select.Option key={m} value={m}>
											{m}
										</Select.Option>
									))}
								</Select>
							</Col>
							<Col xs={8} md={1}>
								<div className="ApiCallSetupControl">
									<label className="InputLabel">{Strings.generic.active}</label>
									<Switch
										checked={setup.active !== false}
										onChange={(checked: boolean) =>
											updateSetup(index, { active: checked })
										}
									/>
								</div>
							</Col>
							<Col xs={8} md={1}>
								<div className="ApiCallSetupControl">
									<label
										className="InputLabel"
										style={{ visibility: "hidden" }}
									>
										_
									</label>
									<button
										className="ApiCallHelpButton"
										onClick={openHelpModal}
										title={Strings.businesses.apiCallHelpTitle}
									>
										<QuestionCircleOutlined />
									</button>
								</div>
							</Col>
							<Col xs={8} md={1}>
								<div className="ApiCallSetupControl">
									<label
										className="InputLabel"
										style={{ visibility: "hidden" }}
									>
										_
									</label>
									<div className="NotDeliveredDeleteContainer">
										<button
											className="NotDeliveredDeleteButton"
											onClick={() => {
												Modal.confirm({
													title: Strings.generic.delete,
													content: Strings.generic.deleteMessage,
													okText: Strings.generic.confirm,
													cancelText: Strings.generic.cancel,
													onOk: () => {
														this.setState({
															apiCallSetups: apiCallSetups.filter(
																(_: any, i: number) => i !== index
															),
															hasUnsavedFields: true,
														})
													},
												})
											}}
										>
											<Icon name="trash" />
										</button>
									</div>
								</div>
							</Col>
						</Row>

						{/* Row 2: Auth method + conditional auth config */}
						<Row gutter={[10, 10]} style={{ marginBottom: 8 }}>
							<Col xs={24} md={6}>
								<label className="InputLabel">
									{Strings.businesses.apiCallAuthMethod}
								</label>
								<Select
									style={{ width: "100%" }}
									value={setup.authMethod || "none"}
									onChange={(value: string) =>
										updateSetup(index, { authMethod: value, authConfig: {} })
									}
								>
									<Select.Option value="none">
										{Strings.businesses.apiCallAuthNone}
									</Select.Option>
									<Select.Option value="apiKey">
										{Strings.businesses.apiCallAuthApiKey}
									</Select.Option>
									<Select.Option value="bearer">
										{Strings.businesses.apiCallAuthBearer}
									</Select.Option>
									<Select.Option value="basic">
										{Strings.businesses.apiCallAuthBasic}
									</Select.Option>
								</Select>
							</Col>

							{setup.authMethod === "apiKey" && (
								<>
									<Col xs={24} md={9}>
										<label className="InputLabel">
											{Strings.businesses.apiCallHeaderName}
										</label>
										<Input
											placeholder="X-API-Key"
											value={setup.authConfig?.headerName || ""}
											onChange={(e: any) =>
												updateAuthConfig(index, { headerName: e.target.value })
											}
										/>
									</Col>
									<Col xs={24} md={9}>
										<label className="InputLabel">
											{Strings.businesses.apiCallApiKey}
										</label>
										<Input.Password
											placeholder={Strings.businesses.apiCallApiKey}
											value={setup.authConfig?.apiKey || ""}
											onChange={(e: any) =>
												updateAuthConfig(index, { apiKey: e.target.value })
											}
										/>
									</Col>
								</>
							)}

							{setup.authMethod === "bearer" && (
								<Col xs={24} md={18}>
									<label className="InputLabel">
										{Strings.businesses.apiCallBearerToken}
									</label>
									<Input.Password
										placeholder={Strings.businesses.apiCallBearerToken}
										value={setup.authConfig?.token || ""}
										onChange={(e: any) =>
											updateAuthConfig(index, { token: e.target.value })
										}
									/>
								</Col>
							)}

							{setup.authMethod === "basic" && (
								<>
									<Col xs={24} md={9}>
										<label className="InputLabel">
											{Strings.businesses.apiCallUsername}
										</label>
										<Input
											placeholder={Strings.businesses.apiCallUsername}
											value={setup.authConfig?.username || ""}
											onChange={(e: any) =>
												updateAuthConfig(index, { username: e.target.value })
											}
										/>
									</Col>
									<Col xs={24} md={9}>
										<label className="InputLabel">
											{Strings.businesses.apiCallPassword}
										</label>
										<Input.Password
											placeholder={Strings.businesses.apiCallPassword}
											value={setup.authConfig?.password || ""}
											onChange={(e: any) =>
												updateAuthConfig(index, { password: e.target.value })
											}
										/>
									</Col>
								</>
							)}
						</Row>

						{/* Row 3: Body Schema JSON editor */}
						<Row gutter={[10, 10]}>
							<Col xs={24}>
								<label className="InputLabel">
									{Strings.businesses.apiCallBodySchema}
								</label>
								<Input.TextArea
									className="ApiCallBodySchema"
									rows={5}
									value={setup.bodySchema ?? "{}"}
									onChange={(e: any) =>
										updateSetup(index, { bodySchema: e.target.value })
									}
									onBlur={(e: any) => {
										try {
											const parsed = JSON.parse(e.target.value)
											updateSetup(index, {
												bodySchema: JSON.stringify(parsed, null, 2),
											})
										} catch {
											notification.warn({
												message: Strings.errors.invalidFields,
												description: Strings.businesses.apiCallInvalidJson,
												placement: "bottomRight",
												duration: 3,
											})
										}
									}}
								/>
							</Col>
						</Row>
					</div>
				))}
			</ContentWrapper>
		)
	}

	async testNotification(templateId: string) {
		const { match } = this.props

		this.setState((prev: any) => ({
			testingNotification: { ...prev.testingNotification, [templateId]: true },
		}))

		try {
			const response = await API.post({
				url: Endpoints.uriNotificationTemplateTest(templateId),
				data: { businessId: match?.params?.id },
			})

			if (response.ok) {
				notification.success({
					message: Strings.settings.notificationTemplates,
					description: Strings.businesses.notificationTestSent,
					placement: "bottomRight",
					duration: 5,
				})
			} else {
				notification.error({
					message: Strings.settings.notificationTemplates,
					description:
						response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.settings.notificationTemplates,
				description: Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}

		this.setState((prev: any) => ({
			testingNotification: { ...prev.testingNotification, [templateId]: false },
		}))
	}

	renderNotifications() {
		const {
			notifications = {},
			allNotificationTemplates = [],
			testingNotification = {},
			availableEvents = {},
		} = this.state

		const lang = this.props.language || "pt"

		const NOTIFICATION_TYPES = Object.entries(availableEvents).map(
			([key, config]: [string, any]) => ({
				key,
				label: config.label?.[lang] || config.label?.pt || key,
				description: config.description?.[lang] || config.description?.pt || "",
			})
		)

		return (
			<ContentWrapper>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						borderBottom: "0.5px solid #f0f0f0",
						paddingBottom: 10,
						marginBottom: 16,
						gap: 4,
					}}
				>
					<Icon
						name="notification"
						style={{ fontSize: 22, marginRight: 15, color: "#075aae" }}
					/>
					<span
						style={{
							fontSize: "1.5em",
							fontWeight: "bold",
							color: "#075aae",
							marginRight: "auto",
						}}
					>
						{Strings.businesses.notificationsTab}
					</span>
				</div>
				<div className="NotificationEventList">
					{NOTIFICATION_TYPES.map(({ key, label, description }, index) => {
						const currentVal = (notifications as any)?.[key]
						const currentId = currentVal?._id || currentVal || null
						const isLast = index === NOTIFICATION_TYPES.length - 1

						return (
							<div
								key={key}
								className="NotificationEventRow"
								style={!isLast ? { borderBottom: "1px solid #f0f0f0" } : {}}
							>
								<div className="NotificationEventInfo">
									<span className="NotificationEventLabel">{label}</span>
									{description && (
										<span className="NotificationEventDesc">{description}</span>
									)}
								</div>
								<div className="NotificationEventActions">
									<Select
										style={{ flex: 1 }}
										placeholder="—"
										allowClear
										showSearch
										filterOption={(input: any, option: any) =>
											option.children
												.toLowerCase()
												.indexOf(input.toLowerCase()) >= 0
										}
										value={currentId || null}
										onChange={(value: any) => {
											this.setState((prev: any) => ({
												notifications: {
													...(prev.notifications || {}),
													[key]: value || null,
												},
												hasUnsavedFields: true,
											}))
										}}
									>
										{(allNotificationTemplates || [])
											.filter((t: any) => t.event === key)
											.map((t: any) => (
												<Select.Option key={t._id} value={t._id}>
													{t.name}
												</Select.Option>
											))}
									</Select>
									<button
										className={`NotificationTestBtn${!currentId ? " --disabled" : ""}`}
										disabled={!currentId || !!testingNotification[currentId]}
										onClick={() => this.testNotification(currentId)}
									>
										{testingNotification[currentId] ? (
											<span className="NotificationTestSpinner" />
										) : (
											<Icon name="paper-plane" />
										)}
										{Strings.businesses.notificationTestBtn}
									</button>
								</div>
							</div>
						)
					})}
				</div>
			</ContentWrapper>
		)
	}

	render() {
		const { activeTab } = this.state
		const { match } = this.props

		return (
			<div className="ScreenClientDetail">
				<Helmet>
					<title>{Strings.sidebar.businesses}</title>
					<meta
						name="description"
						content="Define your business information and settings"
					/>
				</Helmet>
				<ContentWrapper>
					<Tabs
						defaultActiveKey="info"
						onChange={(key: string) => this.setState({ activeTab: key })}
					>
						<Tabs.TabPane
							tab={
								<div className="TabIconContainer">
									<Icon name="partner" />
									<p style={{ margin: 0 }}>{Strings.businesses.info}</p>
								</div>
							}
							key="info"
						/>
						<Tabs.TabPane
							tab={
								<div className="TabIconContainer">
									<Icon name="file" />
									<p style={{ margin: 0 }}>{Strings.businesses.contracts}</p>
								</div>
							}
							disabled={match?.params?.id === "new"}
							key="contracts"
						/>
						<Tabs.TabPane
							tab={
								<div className="TabIconContainer">
									<Icon name="notification" />
									<p style={{ margin: 0 }}>
										{Strings.businesses.notificationsTab}
									</p>
								</div>
							}
							disabled={match?.params?.id === "new"}
							key="notifications"
						/>
					</Tabs>
					{activeTab === "info" && this.renderGeneralInfo()}
					{activeTab === "contracts" && this.renderContracts()}
					{activeTab === "notifications" && this.renderNotifications()}
				</ContentWrapper>
				{activeTab === "info" && this.renderNotDeliveredReasons()}
				{/* {activeTab === "info" && this.renderApiCallSetup()} */}
			</div>
		)
	}
}

const mapStateToProps = (state: any) => ({
	language: state.language,
	selectedBusiness: state.selectedBusiness,
	user: state.user,
})

export default connect(mapStateToProps)(BusinessDetail)
